import { observer } from 'mobx-react-lite'
import { SingleItemSelector } from '@/components/SingleItemSelector'
import { Avatar } from '@/components/Avatar'
import { FormGroupContainer } from '@/components/FormContainer'
import { FormRow } from '@/components/Form'
import { AddUserToWorkspaceDialog } from '@/features/user-management/AddUserToWorkspaceDialog'
import { type AddUserToWorkspaceDialogViewModel } from './AddUserToWorkspaceDialogViewModel'
import { SkillsSelectorContainer } from '@/features/user-management/components/SkillsSelectorContainer'

export interface AddUserToWorkspaceDialogContainerProps {
  vm: AddUserToWorkspaceDialogViewModel
}

export const AddUserToWorkspaceDialogContainer = observer(
  function AddUserToWorkspaceDialogContainer({
    vm,
  }: AddUserToWorkspaceDialogContainerProps) {
    const testId = 'add-user-to-workspace'

    return (
      <AddUserToWorkspaceDialog
        showing={vm.modal.showing}
        onClose={vm.modal.close}
        onSubmit={vm.submit}
        isSubmitPending={vm.submit.pending}
      >
        <FormRow>
          <FormGroupContainer
            label="Workspace"
            error={vm.validation.getError('workspace')}
          >
            <SingleItemSelector
              value={vm.workspace}
              itemOptions={vm.availableWorkspaces}
              itemPrefix={(item) => (
                <Avatar
                  subject={{
                    avatar: item?.branding.logos.icon,
                  }}
                  size={24}
                />
              )}
              onChange={vm.setWorkspace}
              itemToString={(item) => (item ? item.name : '')}
              placeholder="Select workspace"
              isDisabled={vm.submit.pending}
              testId={`${testId}-workspace-selector`}
            />
          </FormGroupContainer>
        </FormRow>

        {vm.workspace && (
          <>
            <FormRow>
              <FormGroupContainer
                label="Role"
                error={vm.validation.getError('workspaceRole')}
              >
                <SingleItemSelector
                  value={vm.workspaceRole}
                  itemOptions={vm.workspaceRoles}
                  onChange={vm.setWorkspaceRole}
                  itemToString={(item) => (item ? item.name : '')}
                  placeholder="Select workspace role"
                  isDisabled={vm.submit.pending}
                  testId={`${testId}-workspace-role-selector`}
                />
              </FormGroupContainer>
            </FormRow>

            <FormRow>
              <FormGroupContainer label="Skills" sublabel="(optional)">
                <SkillsSelectorContainer
                  vm={vm}
                  isDisabled={vm.submit.pending}
                  testId={`${testId}-skills-selector`}
                />
              </FormGroupContainer>
            </FormRow>

            {vm.currentUserTeamWorkspaces.includes(vm.workspace) && (
              <>
                <FormRow>
                  <FormGroupContainer label="Team" sublabel="(optional)">
                    <SingleItemSelector
                      value={vm.team}
                      itemOptions={vm.teams}
                      onChange={vm.setTeam}
                      itemToString={(item) => (item ? item.name : '')}
                      placeholder="Select team"
                      isDisabled={vm.submit.pending}
                      testId={`${testId}-team-selector`}
                    />
                  </FormGroupContainer>
                </FormRow>

                {vm.team && (
                  <FormRow>
                    <FormGroupContainer
                      label="Team role"
                      error={vm.validation.getError('teamRole')}
                    >
                      <SingleItemSelector
                        value={vm.teamRole}
                        itemOptions={vm.teamRoles}
                        onChange={vm.setTeamRole}
                        itemToString={(item) => (item ? item.name : '')}
                        placeholder="Select team role"
                        isDisabled={vm.submit.pending}
                        testId={`${testId}-team-role-selector`}
                      />
                    </FormGroupContainer>
                  </FormRow>
                )}
              </>
            )}
          </>
        )}
      </AddUserToWorkspaceDialog>
    )
  },
)
